// Számlázási-profil create/edit — jobb oldali beúszó FormSheet (az asset-form-sheet
// mintája). Egy komponens KÉT módban: `profile == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PUT If-Match/
// OCC). A validálás forrás-igazsága a @atlas/contracts BillingProfileWriteBodySchema (a resolver a
// features/billing-profiles/form.ts sémáján át fut). Create-nél az ügyfél a refs listájából, KERESŐS comboboxszal;
// edit-nél az ügyfél ZÁROLT (read-only).
//
// AZ „ELSŐDLEGES" KAPCSOLÓ MINDKÉT MÓDBAN OTT VAN. Korábban CSAK create-nél látszott, editnél
// eltűnt — ez két bajt okozott: (1) a form nem volt egységes, (2) az ÜGYFÉL-DETAIL oldalán EGYÁLTALÁN nem
// lehetett elsődlegest váltani, mert a dedikált csillag-action csak a lista-oldalon van. A legacy CRM is
// ugyanazt a modalt használta mindkét módban, a checkboxszal együtt.
//
// A KAPCSOLÓ SZEMANTIKÁJA (az „egy ügyfél = pontosan egy elsődleges" invariánsból következik):
//   • MÁR elsődleges profil → a kapcsoló ZÁROLT (bekapcsolva). Kikapcsolni nem lehet: az nulla elsődlegest
//     hagyna, amiről nem állítható ki számla. A váltás útja, hogy egy MÁSIK profilt teszünk elsődlegessé —
//     az automatikusan lefokozza ezt. A hint ezt meg is mondja.
//   • NEM elsődleges profil → a kapcsoló szabad: bekapcsolva ez lép elő, a korábbi lefokozódik.
// A szerver ugyanezt kényszeríti (`MSG_PRIMARY_UNDEMOTABLE`) — a UI itt a fal TÜKRE, nem helyettesítője.
import { type BillingProfileRowSchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { type ComboboxOption } from '@/components/shared/entity-combobox';
import {
  ComboboxField,
  SwitchField,
  TextField,
  TextareaField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { ManagingDirectorsField } from './managing-directors-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { billingProfileKeys } from '@/features/billing-profiles/api';
import {
  billingProfileFormSchema,
  billingProfileFormToCreateBody,
  billingProfileFormToWriteBody,
  billingProfileToFormValues,
  buildBillingProfileErrorMap,
  EMPTY_BILLING_PROFILE_FORM,
  type BillingProfileFormValues,
} from '@/features/billing-profiles/form';
import { api } from '@/lib/api';
import { countryLabel, countryOptions } from '@/lib/country-options';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type BillingProfileRow = z.infer<typeof BillingProfileRowSchema>;

export interface BillingProfileFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő profil (hordozza az OCC `version`-t + az előtöltést). */
  profile: BillingProfileRow | null;
  /**
   * ELŐTÖLTÖTT, ZÁROLT ügyfél — akkor adjuk meg, ha a sheet egy KONKRÉT ügyfél detail-oldaláról nyílik
   * („hozzáadás" a kapcsolódó szekcióból). Ilyenkor az ügyfél nem választható: a szülő adott, és egy
   * szabadon átállítható választó itt csak hibalehetőség (a rekord véletlenül MÁS ügyfélhez kerülne).
   * A megjelenítés ugyanaz, mint szerkesztéskor: olvasható név, nem beviteli mező.
   */
  lockedClient?: { id: string; name: string } | undefined;
}

export function BillingProfileFormSheet({
  open,
  onOpenChange,
  profile,
  lockedClient,
}: BillingProfileFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = profile !== null;
  /** Ez a profil MA elsődleges? Ilyenkor a kapcsoló zárolt — lefokozni csak másik előléptetésével lehet. */
  const isCurrentPrimary = profile?.isPrimary === true;
  const locale = i18n.language;

  // Ügyfél-választó refs (CSAK create-módban kell — a jog-kapu billing_profiles.manage; a BFF adja).
  const refsQ = api.billingProfileClientRefs.useQuery(
    billingProfileKeys.clientRefs(),
    {},
    { queryKey: billingProfileKeys.clientRefs(), enabled: open && !isEdit },
  );
  const clients = refsQ.data?.status === 200 ? refsQ.data.body.clients : [];

  const form = useForm<BillingProfileFormValues>({
    resolver: zodResolver(billingProfileFormSchema, {
      errorMap: buildBillingProfileErrorMap(t),
    }) as Resolver<BillingProfileFormValues>,
    defaultValues: EMPTY_BILLING_PROFILE_FORM,
    // A hiba már MENTÉS ELŐTT látszik: a mező elhagyásakor (blur) validál, utána gépelés közben frissül
    // (RHF `reValidateMode` default: onChange) — nem „kiabál" az első karakternél, de nem is csak mentéskor.
    mode: 'onTouched',
  });
  const { reset, handleSubmit, setError } = form;

  // A form a megnyitott profil értékeire áll (a sheet újranyitásakor is friss); create → üres.
  useEffect(() => {
    if (profile) reset(billingProfileToFormValues(profile));
    // ZÁROLT ügyfél (a szülő detail-oldaláról nyitva): a create-form MÁR a szülővel indul — a `clientId`
    // így a rejtett választó nélkül is kimegy a body-ban, és a kliens-oldali „válassz ügyfelet" őr sem üt be.
    else reset({ ...EMPTY_BILLING_PROFILE_FORM, clientId: lockedClient?.id ?? '' });
  }, [profile, lockedClient, reset]);

  const mutation = useEntityMutation<BillingProfileFormValues>({
    mutationFn: (values) =>
      isEdit
        ? api.billingProfileUpdate.mutation({
            params: { id: profile.id },
            body: billingProfileFormToWriteBody(values),
            headers: { 'if-match': String(profile.version) },
          })
        : api.billingProfileCreate.mutation({ body: billingProfileFormToCreateBody(values) }),
    invalidateKeys: [billingProfileKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_BILLING_PROFILE_FORM) {
        setError(field as keyof BillingProfileFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:billingProfiles.updated') : t('toast:billingProfiles.created'),
      conflict: t('toast:billingProfiles.conflict'),
      forbidden: t('toast:billingProfiles.forbidden'),
      validation: t('toast:billingProfiles.validation'),
      error: t('toast:billingProfiles.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    // A clientId csak create-kor kötelező (edit-nél zárolt) — kliens-oldali őr a 422 előtt.
    if (!isEdit && values.clientId.trim() === '') {
      setError('clientId', { message: t('validation:billingProfiles.client') });
      return;
    }
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  // A keresős ügyfélválasztó forrása: a betöltött refs (a lista már minden ügyfelet hoz) HELYBEN szűrve —
  // nincs külön kereső-endpoint, a nagy listát is típusod-kereséssel lehet szűkíteni.
  const searchClients = useCallback(
    (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      const matched = needle
        ? clients.filter((c) => c.name.toLowerCase().includes(needle))
        : clients;
      return Promise.resolve(matched.slice(0, 50).map((c) => ({ id: c.id, label: c.name })));
    },
    [clients],
  );

  // Ország-választó: a teljes ISO-lista a UI nyelvén (kód→név `Intl.DisplayNames`), helyben szűrve. A form a
  // KÓDOT tárolja; az `initialOption` az induló érték (edit → a sor kódja; create → 'HU') lokalizált neve.
  const countryOpts = useMemo(() => countryOptions(locale), [locale]);
  const searchCountries = useCallback(
    (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      return Promise.resolve(
        needle ? countryOpts.filter((o) => o.label.toLowerCase().includes(needle)) : countryOpts,
      );
    },
    [countryOpts],
  );
  const countryInitial = useMemo<ComboboxOption>(() => {
    const code = profile?.countryCode ?? 'HU';
    return { id: code, label: countryLabel(code, locale) };
  }, [profile, locale]);

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_BILLING_PROFILE_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('billingProfiles.editTitle') : t('billingProfiles.createTitle')}
      description={isEdit ? (profile.billingName ?? undefined) : t('billingProfiles.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('billingProfiles.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('billingProfiles.saving') : t('billingProfiles.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {isEdit || lockedClient ? (
          <div>
            <p className="text-sm font-medium">{t('billingProfiles.form.client')}</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {isEdit ? (profile.clientName ?? '—') : (lockedClient?.name ?? '—')}
            </p>
          </div>
        ) : (
          <ComboboxField<BillingProfileFormValues>
            name="clientId"
            label={t('billingProfiles.form.client')}
            required
            placeholder={t('billingProfiles.form.clientPlaceholder')}
            search={searchClients}
          />
        )}

        <TextField<BillingProfileFormValues>
          name="billingName"
          label={t('billingProfiles.form.billingName')}
          required
          autoComplete="off"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<BillingProfileFormValues>
            name="taxNumber"
            label={t('billingProfiles.form.taxNumber')}
            autoComplete="off"
          />
          <TextField<BillingProfileFormValues>
            name="euTaxNumber"
            label={t('billingProfiles.form.euTaxNumber')}
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <ComboboxField<BillingProfileFormValues>
            name="countryCode"
            label={t('billingProfiles.form.countryCode')}
            required
            search={searchCountries}
            initialOption={countryInitial}
            clearable={false}
            placeholder={t('billingProfiles.form.countryPlaceholder')}
          />
          <TextField<BillingProfileFormValues>
            name="zipCode"
            label={t('billingProfiles.form.zipCode')}
            required
            autoComplete="off"
          />
          <div className="sm:col-span-2">
            <TextField<BillingProfileFormValues>
              name="city"
              label={t('billingProfiles.form.city')}
              required
              autoComplete="off"
            />
          </div>
        </div>

        <TextField<BillingProfileFormValues>
          name="address"
          label={t('billingProfiles.form.address')}
          required
          autoComplete="off"
        />

        {/* ÜGYVEZETŐ-TÖRTÉNET — ismételhető sorok tól–ig időszakkal (a korábbi egy-mezős „Ügyvezető" helyén). */}
        <ManagingDirectorsField form={form} />

        <TextField<BillingProfileFormValues>
          name="billingEmails"
          label={t('billingProfiles.form.billingEmails')}
          hint={t('billingProfiles.form.billingEmailsHint')}
          autoComplete="off"
        />

        <TextField<BillingProfileFormValues>
          name="defaultInvoicePaymentDeadlineDays"
          label={t('billingProfiles.form.paymentDeadline')}
          hint={t('billingProfiles.form.paymentDeadlineHint')}
          autoComplete="off"
        />

        {/* EGY számlakép-megjegyzés (a bizonylat egy megjegyzés-blokkot nyomtat) — a számla-űrlap ezt tölti elő. */}
        <TextareaField<BillingProfileFormValues>
          name="defaultInvoiceNote"
          label={t('billingProfiles.form.note')}
          hint={t('billingProfiles.form.noteHint')}
          rows={3}
        />

        {/* Elsődleges — MINDKÉT módban látszik (lásd a fájl fejlécét). Az elsődleges profilon ZÁROLT:
            kikapcsolni nem lehet, csak másikat előléptetni. */}
        <SwitchField<BillingProfileFormValues>
          name="isPrimary"
          label={t('billingProfiles.form.isPrimary')}
          hint={
            isCurrentPrimary
              ? t('billingProfiles.form.isPrimaryLockedHint')
              : t('billingProfiles.form.isPrimaryHint')
          }
          disabled={isCurrentPrimary}
        />
      </Form>
    </FormSheet>
  );
}
